import { Routes } from '@angular/router';
import { EnhancedAuthGuard } from 'src/app/core/guards/enhanced-auth.guard';
import { RouteData } from 'src/app/core/models/route-data';

/**
 * Signup and invite routes. All unauthenticated; signup and inviteSignup are parent
 * routes with custom headers/footers for the multi-outlet pattern.
 */
export const SIGNUP_INVITE_ROUTES: Routes = [
    // Note: signup is an abstract parent route in UI-Router with custom header/footer
    {
        path: 'signup',
        loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'signup',
            unauthenticated: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/signup/header/signup-header.component').then(m => m.SignupHeaderComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'sideRail'
            },
            {
                path: '',
                loadComponent: () => import('src/components/signup/footer/signup-footer.component').then(m => m.SignupFooterComponent),
                outlet: 'footer'
            }
        ]
    },

    {
        path: 'signup/activation',
        loadComponent: () => import('src/components/signup/activation/activation.component').then(m => m.ActivationComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'signupActivation',
            unauthenticated: true
        } satisfies RouteData
    },

    {
        path: 'signup/acceptInvitation',
        loadComponent: () => import('src/components/signup/connection/accept-invitation.component').then(m => m.AcceptInvitationComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'signupAcceptInvitation',
            unauthenticated: true
        } satisfies RouteData
    },

    // Legacy redirect: /invite-signup was used in old UI-Router code with query params
    // Redirect to inviteSignup (camelCase) for consistency
    {
        path: 'invite-signup',
        redirectTo: '/inviteSignup/initiateInvite',
        pathMatch: 'full'
    },

    // Note: inviteSignup is an abstract parent route with custom public header
    {
        path: 'inviteSignup',
        loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'inviteSignup',
            unauthenticated: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/header/public-header-black-logo.component').then(m => m.PublicHeaderBlackLogoComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'inviteSignup/landing',
        loadComponent: () => import('src/components/invite-signup/landing/invite-landing.component').then(m => m.InviteSignupLandingComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'inviteSignupLanding',
            unauthenticated: true
        } satisfies RouteData
    },

    {
        path: 'inviteSignup/forwardEmail',
        loadComponent: () => import('src/components/invite-signup/forward-email/forward-email.component').then(m => m.ForwardEmailComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'inviteSignupForwardEmail',
            unauthenticated: true
        } satisfies RouteData
    },

    {
        path: 'inviteSignup/initiateInvite',
        loadComponent: () => import('src/components/invite-signup/initiate-invite/initiate-invite.component').then(m => m.InitiateInviteComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'inviteSignupInitiateInvite',
            unauthenticated: true
        } satisfies RouteData
    },

    {
        path: 'inviteSignup/initiatePending',
        loadComponent: () => import('src/components/invite-signup/initiate-pending/initiate-pending.component').then(m => m.InitiatePendingComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'inviteSignupInitiatePending',
            unauthenticated: true
        } satisfies RouteData
    },

    {
        path: 'inviteSignup/inviteToken',
        loadComponent: () => import('src/components/invite-signup/invite-token/invite-token.component').then(m => m.InviteTokenComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'inviteSignupInviteToken',
            unauthenticated: true
        } satisfies RouteData
    },

    {
        path: 'inviteSignup/signup',
        loadComponent: () => import('src/components/invite-signup/invite-signup.component').then(m => m.InviteSignupComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'inviteSignupSignup',
            unauthenticated: true
        } satisfies RouteData
    },

    {
        path: 'inviteSignup/processInvite',
        loadComponent: () => import('src/components/invite-signup/process-invite/process-invite.component').then(m => m.ProcessInviteComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'inviteSignupProcessInvite',
            unauthenticated: true
        } satisfies RouteData
    },

    {
        path: 'inviteSignup/clearInvite',
        loadComponent: () => import('src/components/invite-signup/process-invite/process-invite.component').then(m => m.ProcessInviteComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'inviteSignupClearInvite',
            unauthenticated: true
        } satisfies RouteData
    },

    {
        path: 'inviteSignup/organizationSignup',
        loadComponent: () => import('src/components/invite-signup/invite-organization-signup/invite-organization-signup.component').then(m => m.InviteOrganizationSignupComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'inviteSignupOrganizationSignup',
            unauthenticated: true
        } satisfies RouteData
    },

    {
        path: 'inviteSignup/previousAccountDetected',
        loadComponent: () => import('src/components/invite-signup/previous-account-detected/previous-account-detected.component').then(m => m.PreviousAccountDetectedComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'inviteSignupPreviousAccountDetected',
            unauthenticated: true
        } satisfies RouteData
    },

    {
        path: 'agencyDecline',
        loadComponent: () => import('src/components/agency-decline/agency-decline.component').then(m => m.AgencyDeclineComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            helpRouteName: 'agencyDecline',
            unauthenticated: true
        } satisfies RouteData
    }
];
